import { Button, Label, ScrollView, Text, View, XStack, YStack } from "tamagui";
import { useAtom } from "jotai";
import { Ride } from "./types";
import { rideAtom } from "./atoms";
import {
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  TextInput as RNTextInput,
} from "react-native";
import { Input, TextArea } from "../design-system";
import { ArrowRightIcon } from "../design-system/icons/ArrowRightIcon";
import {
  PillSelect,
  PillSelectOption,
} from "../design-system/PillSelect/PillSelect";
import DateTimePicker from "@react-native-community/datetimepicker";
import { format, parse } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { ScrollView as RNScrollView } from "react-native";
import { LocationModal } from "./components/LocationModal";
import { RouteModal } from "./components/RouteModal";
import { brandShadow, brandShadowSm } from "../../../tamagui.config";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const terrainOptions: PillSelectOption[] = [
  { label: "Road", value: "Road" },
  { label: "Gravel", value: "Gravel" },
  { label: "Mixed", value: "Mixed" },
  { label: "MTB", value: "MTB" },
];
const paceOptions: PillSelectOption[] = [
  { label: "A", value: "A" },
  { label: "B", value: "B" },
  { label: "C", value: "C" },
  { label: "D", value: "D" },
  { label: "Party", value: "Party" },
];

type RideFormProps = {
  handleSubmit: (ride: Ride) => void;
  currentUserId: string;
  defaultValues?: Ride;
  isEditing?: boolean;
  footer?: React.ReactNode;
};

export const RideForm = (props: RideFormProps) => {
  return <RideFormInner {...props} />;
};

export const RideFormInner = ({
  defaultValues,
  handleSubmit,
  currentUserId,
  isEditing,
  footer,
}: RideFormProps) => {
  const [ride, setRide] = useAtom(rideAtom);

  const scrollRef = useRef<RNScrollView>(null);

  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isRouteModalOpen, setIsRouteModalOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);

  useEffect(() => {
    if (defaultValues) setRide(defaultValues);
  }, [defaultValues]);

  const { name, description, terrain, groups } = ride ?? {};
  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    if (isEditing) {
      if (defaultValues?.date && defaultValues?.time) {
        const date = parse(defaultValues?.date ?? "", "yyyy-MM-dd", new Date());
        const time = parse(defaultValues?.time ?? "", "HH:mm:ss", new Date());
        setDate(date);
        setTime(time);
      }
    }
  }, [isEditing, defaultValues]);

  const canSubmit =
    !!name && !!ride?.location && !!terrain && !!ride.groups?.length;

  const onSubmit = () => {
    const rideInput = {
      ...ride,
      time: ride?.time ?? format(new Date(), "HH:mm:ss"),
      date: ride?.date ?? format(new Date(), "yyyy-MM-dd"),
    };
    if (rideInput) handleSubmit(rideInput);
  };

  const { bottom } = useSafeAreaInsets();

  return (
    <>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        keyboardVerticalOffset={bottom + 20}
      >
      <ScrollView
        ref={scrollRef as any}
        flex={1}
        keyboardShouldPersistTaps="handled"
        bg="$brandParchment"
        contentContainerStyle={{
          paddingBottom: bottom + 20,
        }}
      >
          <YStack gap="$1" flex={1} w="100%" px="$4">

            {/* Primary fields */}
            <View>
              <Label onPress={() => Keyboard.dismiss()}>Name</Label>
              <Input
                placeholder="Name"
                value={name ?? ""}
                onChangeText={(text) => {
                  setRide({ ...ride, name: text });
                }}
              />
            </View>
            <View>
              <Label>Where are you starting?</Label>
              <Input
                placeholder="Where are you starting?"
                value={ride?.location ?? ""}
                onChangeText={(text) => {
                  setRide({ ...ride, location: text });
                }}
              />
            </View>

            <XStack justifyContent="space-between" alignItems="center" mt="$4">
              <Label onPress={() => Keyboard.dismiss()}>Date</Label>
              {Platform.OS === "android" ? (
                <Pressable onPress={() => setIsDateModalOpen(true)}>
                  <Text fontSize={16}>{format(date, "MM/dd/yyyyy")}</Text>
                </Pressable>
              ) : null}
              {isDateModalOpen || Platform.OS !== "android" ? (
                <DateTimePicker
                  testID="dateTimePicker"
                  value={date}
                  mode={"date"}
                  is24Hour={false}
                  onChange={(e, d) => {
                    setIsDateModalOpen(false);
                    if (d) {
                      setDate(d);
                      const formattedDate = format(d, "yyyy-MM-dd");
                      setRide({ ...ride, date: formattedDate });
                    }
                  }}
                />
              ) : null}
            </XStack>
            <XStack justifyContent="space-between" alignItems="center">
              <Label onPress={() => Keyboard.dismiss()}>Time</Label>
              {Platform.OS === "android" ? (
                <Pressable onPress={() => setIsTimeModalOpen(true)}>
                  <Text fontSize={16}>{format(time, "hh:mm a")}</Text>
                </Pressable>
              ) : null}
              {isTimeModalOpen || Platform.OS !== "android" ? (
                <DateTimePicker
                  value={time}
                  mode={"time"}
                  is24Hour={false}
                  display="default"
                  onChange={(e, t) => {
                    setIsTimeModalOpen(false);
                    if (t) {
                      setTime(t);
                      const formattedTime = format(t, "HH:mm:ss");
                      setRide({ ...ride, time: formattedTime });
                    }
                  }}
                />
              ) : null}
            </XStack>

            <View>
              <PillSelect
                label="Pace"
                value={groups?.[0] ?? ""}
                setValue={(string) =>
                  setRide({ ...ride, groups: [string as any] })
                }
                options={paceOptions}
              />
            </View>

            {/* More Options toggle */}
            <Pressable onPress={() => setShowMore((v) => !v)}>
              <XStack alignItems="center" gap="$2" mt="$4" mb="$2">
                <View bg="black" h={1} flex={1} />
                <Text fontFamily="$heading" fontSize={12} textTransform="uppercase">
                  {showMore ? "Less ▴" : "More Options ▾"}
                </Text>
                <View bg="black" h={1} flex={1} />
              </XStack>
            </Pressable>

            {/* Collapsed fields */}
            {showMore ? (
              <YStack gap="$1">
                <View>
                  <Label>Where are you going?</Label>
                  <Button
                    bg="white"
                    width="100%"
                    justifyContent="space-between"
                    onPress={() => setIsRouteModalOpen(true)}
                    pressStyle={{ backgroundColor: "$brandParchment" }}
                    py="$4"
                    borderWidth={1}
                    borderColor="black"
                    {...brandShadowSm}
                    h={60}
                  >
                    <Text color={ride?.route?.id ? "black" : "rgba(0,0,0,0.70)"}>
                      {ride?.route?.name ?? "Choose a route"}
                    </Text>
                    <ArrowRightIcon width={20} height={20} />
                  </Button>
                </View>

                <View>
                  <PillSelect
                    label="Terrain"
                    value={terrain ?? ""}
                    setValue={(string) =>
                      setRide({ ...ride, terrain: string as any })
                    }
                    options={terrainOptions}
                  />
                </View>

                <View>
                  <Label onPress={() => Keyboard.dismiss()}>Description</Label>
                  <TextArea
                    numberOfLines={4}
                    placeholder="Tell us about the ride! (Stops, etc)"
                    value={description ?? ""}
                    style={{
                      backgroundColor: "white",
                      borderWidth: 1,
                      borderRadius: 6,
                      borderColor: "black",
                      paddingVertical: 16,
                      paddingHorizontal: 12,
                      fontSize: 18,
                    }}
                    onFocus={() => {
                      setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 150);
                    }}
                    onChangeText={(text) => {
                      setRide({ ...ride, description: text });
                    }}
                  />
                </View>
              </YStack>
            ) : null}

            <Button
              onPress={onSubmit}
              mt="$4"
              opacity={canSubmit ? 1 : 0.5}
              disabled={!canSubmit}
              bg="$brandYellow"
              pressStyle={{
                bg: "$brandYellow",
                shadowColor: "$colorTransparent",
                shadowOffset: { width: 0, height: 0 },
                borderWidth: 1,
                borderColor: "black",
                scale: 0.98,
              }}
              borderWidth={1}
              fontSize={16}
              fontFamily="$heading"
              borderColor={"black"}
              {...brandShadow}
            >
              SUBMIT
            </Button>
            {footer}
          </YStack>
      </ScrollView>
      </KeyboardAvoidingView>

      <LocationModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
      />

      <RouteModal
        isOpen={isRouteModalOpen}
        onClose={() => setIsRouteModalOpen(false)}
        userId={currentUserId}
      />
    </>
  );
};
